/**
 * Pack-size selector for the OOC ProvMAS/ProvNC grids — Checkbox per
 * distinct market packsize, same look/behavior as
 * ProvisionalForecastV2/Edit/PackSizeChips.tsx (reuses its styles),
 * adapted to be prop-driven instead of reading useEditStore() directly
 * since OOC has its own store (oocStore.ts) and data shape.
 *
 * Drives `selectedPackSizes` on OOCProvForecastData via
 * OOCStore.toggleProvPackSelected, which recomputes annualVolSU /
 * historical12MVolume from marketData — see oocStore.ts.
 */

import { observer } from 'mobx-react';
import { Checkbox } from 'primereact/checkbox';

import { OOCMarketDataRow } from 'Interfaces/FDPInterfaces/oocInterfaces';
import { SelectedPackSize } from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
// Reuse the canonical chip styling rather than duplicating it — OOC's
// pack-size row should look identical to Provisional Forecast's.
import styles from '../../ProvisionalForecastV2/Edit/packSizeChips.module.scss';

interface OOCPackSizeChipsProps {
  ndc11: string;
  marketData: OOCMarketDataRow[];
  selectedPackSizes: SelectedPackSize[];
  onToggle: (ndc11: string, packsize: string) => void;
}

const OOCPackSizeChips = observer(
  ({ ndc11, marketData, selectedPackSizes, onToggle }: OOCPackSizeChipsProps) => {
    // Universe of packs = every distinct pack on file for this NDC's GFC,
    // PLUS any saved pack size that isn't (yet, or ever will be) in
    // marketData — matches ProvisionalForecastV2/Edit/PackoutPackRow.tsx's
    // `allPacksForLeft` convention. Without this union, a pack size saved
    // via that screen's free-text "+ Add pack" affordance (Packout NDCs
    // only, no AnnualMarketSales row of its own) never rendered a chip in
    // OOC at all — the saved selection existed but was invisible/unusable.
    const marketPackSizes = Array.from(new Set(marketData.map(row => row.packsize)));
    const userAddedPackSizes = selectedPackSizes
      .map(p => p.packsize)
      .filter(ps => !marketPackSizes.includes(ps));
    const allPackSizes = [...marketPackSizes, ...userAddedPackSizes];

    if (allPackSizes.length === 0) {
      return <div className={styles.empty}>No pack sizes available for this GFC.</div>;
    }

    const isOn = (packsize: string): boolean => {
      const entry = selectedPackSizes.find(p => p.packsize === packsize);
      return entry?.used_in_calc ?? false;
    };

    return (
      <div className={styles.wrap}>
        {allPackSizes.map(packsize => {
          const inputId = `ooc-pack-${ndc11}-${packsize}`;
          return (
            <label key={packsize} htmlFor={inputId} className={styles.chip}>
              <Checkbox
                inputId={inputId}
                checked={isOn(packsize)}
                onChange={() => onToggle(ndc11, packsize)}
              />
              <span className={styles.label}>{formatPackLabel(packsize)}</span>
            </label>
          );
        })}
      </div>
    );
  }
);

/**
 * Mirrors PackSizeChips.formatPackLabel — strips the `.0000` decimal
 * noise the backend stores so operators see the same labels they're
 * used to on the canonical Provisional Forecast screen.
 */
function formatPackLabel(packsize: string): string {
  const n = Number(packsize);
  if (!Number.isFinite(n)) return packsize;
  return Number.isInteger(n) ? String(Math.trunc(n)) : String(n);
}

export default OOCPackSizeChips;
